import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import CustomModal from "components/CustomModal";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import {
  listarRegrasFrete,
  criarRegraFrete,
  atualizarRegraFrete,
  excluirRegraFrete,
  RegraFrete,
  RegraFretePayload,
  TipoDescontoFrete,
} from "services/api";

type Props = { storeId: string };

function descricaoDesconto(regra: Pick<RegraFrete, "tipoDesconto" | "valorDesconto">) {
  if (regra.tipoDesconto === "gratis") return "Grátis";
  if (regra.tipoDesconto === "percentual") return `${regra.valorDesconto ?? 0}% off`;
  return `R$ ${Number(regra.valorDesconto ?? 0).toFixed(2)} off`;
}

const CARRIER_LABEL: Record<string, string> = { UPS: "UPS", FEDEX: "FedEx" };

const EMPTY_FORM: RegraFretePayload = {
  nome: "",
  ativa: true,
  valorMinimo: 0,
  transportadoras: [],
  tipoDesconto: "gratis",
  valorDesconto: null,
  prioridade: 0,
};

export function RegrasFreteManager({ storeId }: Props) {
  const [regras, setRegras] = useState<RegraFrete[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [form, setForm] = useState<RegraFretePayload>(EMPTY_FORM);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listarRegrasFrete(storeId);
      setRegras(data);
    } catch {
      setRegras([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNova = () => {
    setEditandoId(null);
    setForm(EMPTY_FORM);
    setErro(null);
    setShowModal(true);
  };

  const abrirEdicao = (regra: RegraFrete) => {
    setEditandoId(regra.id);
    setForm({
      nome: regra.nome,
      ativa: regra.ativa,
      valorMinimo: regra.valorMinimo,
      transportadoras: regra.transportadoras,
      tipoDesconto: regra.tipoDesconto,
      valorDesconto: regra.valorDesconto,
      prioridade: regra.prioridade,
    });
    setErro(null);
    setShowModal(true);
  };

  const toggleTransportadora = (t: string) => {
    setForm((f) => ({
      ...f,
      transportadoras: f.transportadoras.includes(t)
        ? f.transportadoras.filter((x) => x !== t)
        : [...f.transportadoras, t],
    }));
  };

  const salvar = async () => {
    setErro(null);
    if (!form.nome.trim()) return setErro("Dá um nome pra regra.");
    if (!form.transportadoras.length) return setErro("Escolhe pelo menos uma transportadora.");
    if (form.tipoDesconto !== "gratis" && !form.valorDesconto) {
      return setErro("Informa o valor do desconto.");
    }
    setSalvando(true);
    try {
      if (editandoId) {
        await atualizarRegraFrete(storeId, editandoId, form);
      } else {
        await criarRegraFrete(storeId, form);
      }
      setShowModal(false);
      await carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.erro || "Falha ao salvar a regra.");
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id: number) => {
    if (!window.confirm("Excluir essa regra de frete?")) return;
    await excluirRegraFrete(storeId, id);
    await carregar();
  };

  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-900">Regras de frete</h3>
        <Button
          size="sm"
          onClick={abrirNova}
          className="gap-1.5 bg-brand-primary text-white hover:bg-brand-primary/90"
        >
          <Plus className="h-4 w-4" /> Nova regra
        </Button>
      </div>

      {loading && <p className="text-xs text-slate-400">Carregando...</p>}

      {!loading && regras.length === 0 && (
        <p className="text-xs text-slate-400">
          Nenhuma regra ainda — crie uma pra dar frete grátis ou desconto acima de um valor de compra.
        </p>
      )}

      {!loading && regras.length > 0 && (
        <div className="space-y-2">
          {regras.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{r.nome}</p>
                <p className="text-xs text-slate-500">
                  A partir de R$ {Number(r.valorMinimo).toFixed(2)} ·{" "}
                  {r.transportadoras.map((t) => CARRIER_LABEL[t] || t).join(" + ")} · {descricaoDesconto(r)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge label={r.ativa ? "Ativa" : "Inativa"} tone={r.ativa ? "green" : "slate"} />
                <button onClick={() => abrirEdicao(r)} className="text-slate-400 hover:text-brand-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => excluir(r.id)} className="text-slate-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CustomModal show={showModal} title={editandoId ? "Editar regra" : "Nova regra de frete"} setShow={setShowModal}>
        <div className="p-4 flex flex-col gap-3">
          {erro && <p className="text-xs text-red-600">{erro}</p>}

          <div>
            <label className="text-xs font-medium text-slate-600">Nome da regra</label>
            <input
              className="w-full border rounded px-2 py-1.5 text-sm mt-1"
              placeholder="Ex.: Frete grátis Black Friday"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">A partir de quanto (R$)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              className="w-full border rounded px-2 py-1.5 text-sm mt-1"
              value={form.valorMinimo}
              onChange={(e) => setForm((f) => ({ ...f, valorMinimo: Number(e.target.value) }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Transportadora</label>
            <div className="flex gap-4 mt-1">
              {["UPS", "FEDEX"].map((t) => (
                <label key={t} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={form.transportadoras.includes(t)}
                    onChange={() => toggleTransportadora(t)}
                  />
                  {CARRIER_LABEL[t]}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Tipo de desconto</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm mt-1"
              value={form.tipoDesconto}
              onChange={(e) =>
                setForm((f) => ({ ...f, tipoDesconto: e.target.value as TipoDescontoFrete }))
              }
            >
              <option value="gratis">Frete grátis</option>
              <option value="percentual">% de desconto</option>
              <option value="fixo">Valor fixo de desconto</option>
            </select>
          </div>

          {form.tipoDesconto !== "gratis" && (
            <div>
              <label className="text-xs font-medium text-slate-600">
                {form.tipoDesconto === "percentual" ? "Percentual (%)" : "Valor (R$)"}
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={form.valorDesconto ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, valorDesconto: Number(e.target.value) }))}
              />
            </div>
          )}

          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={form.ativa}
              onChange={(e) => setForm((f) => ({ ...f, ativa: e.target.checked }))}
            />
            Regra ativa
          </label>

          <Button
            onClick={salvar}
            disabled={salvando}
            className="bg-brand-primary text-white hover:bg-brand-primary/90 mt-2"
          >
            {salvando ? "Salvando..." : "Salvar regra"}
          </Button>
        </div>
      </CustomModal>
    </div>
  );
}
